import { useState, useEffect } from 'react'
import { api } from '../../lib/supabase'
import Header from '../../components/feature/Header'

interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  content: string
  category: string
  image_url: string
  author: string
  created_at: string
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('Tümü')

  const categories = ['Tümü', 'Kariyer', 'Kişisel Gelişim', 'İletişim', 'Verimlilik', 'Yaşam Koçluğu', 'Liderlik']

  useEffect(() => {
    // SEO meta tags
    document.title = 'Blog | Koçluk ve Kişisel Gelişim Yazıları | Ankara';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Kişisel gelişim, kariyer koçluğu ve yaşam koçluğu alanlarında faydalı blog yazıları. Ankara merkezli ICF koçu Esin Arifoğlu\'ndan pratik öneriler ve uzman görüşleri.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'koçluk blog, kişisel gelişim yazıları, kariyer koçluğu blog, Ankara koç blog');
    }

    // Schema.org JSON-LD for blog page
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "SeninYolun Koçluk Blog",
      "description": "Kişisel gelişim, kariyer koçluğu ve yaşam koçluğu alanlarında faydalı içerikler",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog`,
      "author": {
        "@type": "Person",
        "name": "Esin Arifoğlu",
        "jobTitle": "ICF Akredite Koç"
      },
      "publisher": {
        "@type": "Organization",
        "name": "SeninYolun Koçluk"
      },
      "inLanguage": "tr-TR",
      "about": [
        "Kişisel Gelişim",
        "Kariyer Koçluğu", 
        "Yaşam Koçluğu",
        "İletişim Becerileri",
        "Verimlilik",
        "Liderlik"
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      document.title = 'Ankara Öğrenci Ebeveyn Kariyer Koçluğu | Esin Arifoğlu ICF Koçu';
    };
  }, []);

  useEffect(() => {
    fetchPosts()
  }, [selectedCategory])

  const fetchPosts = async () => {
    setLoading(true)
    try {
      const result = await api.getBlogPosts(selectedCategory)
      if (result.success) {
        setPosts(result.data)
      }
    } catch (error) {
      console.error('Blog yazıları yüklenirken hata:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Kişisel gelişim, kariyer koçluğu ve yaşam koçluğu alanlarında faydalı içerikler ve pratik öneriler.
          </p>
        </div>
      </section>

      {/* Kategori Filtreleri */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Yazıları */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">Blog yazıları yükleniyor...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Bu kategoride henüz yazı bulunmuyor.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <article key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-block bg-teal-100 text-teal-800 text-sm font-medium px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-teal-600"></i>
                        </div>
                        <span className="text-sm text-gray-700">{post.author}</span>
                      </div>
                      
                      <button className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center space-x-1 cursor-pointer">
                        <span>Devamını Oku</span>
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Yeni Yazılardan Haberdar Olun
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Kişisel gelişim ve kariyer koçluğu alanındaki en güncel içerikleri e-posta ile alın.
          </p>
          
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="E-posta adresiniz"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
            <button
              type="submit"
              className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Abone Ol
            </button>
          </form>
        </div>
      </section>
    </div>
  )
}
